import { FilterFn, Row } from '@tanstack/react-table'
import { compareAsc, parseISO } from 'date-fns'

import { HasID } from '@/types/Id'

export type ConfirmationStatusFilter = 'confirmed' | 'suggested'
export type ConfirmationStatusFilterChoice = ConfirmationStatusFilter | 'all'
export type ConfirmationStatusQueryValue = 'all' | 'suggested'

export const DEFAULT_CONFIRMATION_STATUS_FILTERS: ConfirmationStatusFilter[] = [
  'confirmed',
]

export const CONFIRMATION_STATUS_FILTER_OPTIONS: {
  label: string
  value: ConfirmationStatusFilterChoice
}[] = [
  { label: 'Confirmed', value: 'confirmed' },
  { label: 'Suggested', value: 'suggested' },
  { label: 'All', value: 'all' },
]

export const isConfirmationStatusFilter = (
  value: string,
): value is ConfirmationStatusFilter => {
  return value === 'confirmed' || value === 'suggested'
}

export const isConfirmationStatusFilterChoice = (
  value: string,
): value is ConfirmationStatusFilterChoice => {
  return isConfirmationStatusFilter(value) || value === 'all'
}

export const getEntityConfirmationFetchParams = (
  statuses: ConfirmationStatusFilter[],
): { withConfirmed: boolean; withSuggested: boolean } => {
  const withConfirmed = statuses.includes('confirmed')
  const withSuggested = statuses.includes('suggested')

  if (!withConfirmed && !withSuggested) {
    return { withConfirmed: true, withSuggested: false }
  }

  return { withConfirmed, withSuggested }
}

export const getActiveConfirmationStatusFilters = (
  statuses: ConfirmationStatusFilter[],
): ConfirmationStatusFilterChoice[] => {
  const withConfirmed = statuses.includes('confirmed')
  const withSuggested = statuses.includes('suggested')

  if (withConfirmed && withSuggested) {
    return ['all']
  }
  if (withSuggested) {
    return ['suggested']
  }
  return []
}

export const getNextConfirmationStatusFilters = (
  value: ConfirmationStatusFilterChoice,
): ConfirmationStatusFilter[] => {
  if (value === 'all') {
    return ['confirmed', 'suggested']
  }
  if (value === 'suggested') {
    return ['suggested']
  }
  return ['confirmed']
}

export const isConfirmationStatusFilterOptionChecked = (
  optionValue: ConfirmationStatusFilterChoice,
  statuses: ConfirmationStatusFilter[],
): boolean => {
  const withConfirmed = statuses.includes('confirmed')
  const withSuggested = statuses.includes('suggested')

  if (optionValue === 'all') {
    return withConfirmed && withSuggested
  }
  if (optionValue === 'confirmed') {
    return withConfirmed && !withSuggested
  }
  if (optionValue === 'suggested') {
    return withSuggested && !withConfirmed
  }
  return false
}

export const isConfirmationStatusMenuOptionChecked = (
  optionValue: string,
  statuses: string[],
): boolean => {
  if (!isConfirmationStatusFilterChoice(optionValue)) return false
  return isConfirmationStatusFilterOptionChecked(
    optionValue,
    statuses.filter(isConfirmationStatusFilter),
  )
}

export const getConfirmationStatusFilterLabel = (
  value: ConfirmationStatusFilterChoice,
): string => {
  return value === 'suggested'
    ? 'Suggested'
    : value === 'all'
      ? 'All'
      : 'Confirmed'
}

export const getConfirmationStatusFiltersFromQueryValue = (
  value: string | string[] | undefined,
): ConfirmationStatusFilter[] => {
  const status = Array.isArray(value) ? value[0] : value
  if (status === 'suggested') {
    return ['suggested']
  }
  if (status === 'all') {
    return ['confirmed', 'suggested']
  }
  return [...DEFAULT_CONFIRMATION_STATUS_FILTERS]
}

export const getConfirmationStatusQueryValue = (
  statuses: ConfirmationStatusFilter[],
): ConfirmationStatusQueryValue | undefined => {
  const withConfirmed = statuses.includes('confirmed')
  const withSuggested = statuses.includes('suggested')

  if (withConfirmed && withSuggested) {
    return 'all'
  }
  if (withSuggested) {
    return 'suggested'
  }
  return undefined
}

export const getRowId = <T extends HasID>(row: T) => row.id

export const dateSortFn = <T>(rowA: Row<T>, rowB: Row<T>, columnId: string) => {
  const a = rowA.getValue<string>(columnId)
  const b = rowB.getValue<string>(columnId)

  if (!a && !b) return 0
  if (!a) return -1
  if (!b) return 1

  return compareAsc(parseISO(a), parseISO(b))
}

export const stringArrayIncludes: FilterFn<any> = (
  row,
  columnId,
  filterValue,
) => {
  const value = String(row.getValue(columnId) ?? '')
  return filterValue.includes(value)
}

export const toNull = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

export const sanitizeStringArray = (
  value: string[] | null | undefined,
): string[] | null => {
  const cleaned = value?.map((item) => item.trim()).filter(Boolean) ?? []
  return cleaned.length > 0 ? cleaned : null
}

export {
  arrayIncludesString,
  arrayIncludesUUID,
  stringArrayIntersects,
} from '@/helpers/table'
